import { MigrationInterface, QueryRunner } from 'typeorm';
import { setMigrationRole } from './migration-role';

/**
 * Initial migration for social login linking tables.
 *
 * Tables:
 * - users: Stores users who have linked social accounts. The id is the umsGuid.
 * - accounts: Stores linked provider accounts (Google, Facebook, or 'password' to indicate
 *   the user has a password account in BigCommerce).
 *
 * Note: These tables are ONLY used for social account linking.
 * Normal password login/signup does NOT create records in these tables.
 * Users are only added when they use social login (signup or linking).
 */
export class InitAuthTables1700000000000 implements MigrationInterface {
  name = 'InitAuthTables1700000000000';

  public async up(queryRunner: QueryRunner): Promise<void> {
    await setMigrationRole(queryRunner);
    try {
      // Skip CREATE EXTENSION if it's already installed — Cloud SQL non-superusers
      // hit a permission error even with IF NOT EXISTS, since the privilege check
      // runs before the existence short-circuit.
      const [{ installed }] = await queryRunner.query(
        `SELECT EXISTS (SELECT 1 FROM pg_extension WHERE extname = 'uuid-ossp') AS installed`,
      );
      if (!installed) {
        await queryRunner.query('CREATE EXTENSION "uuid-ossp"');
      }

      // Users table - id is the umsGuid (not auto-generated)
      await queryRunner.query(`
      CREATE TABLE "app"."users" (
        "id" character varying NOT NULL,
        "bcCustomerId" integer NOT NULL,
        "createdAt" TIMESTAMPTZ NOT NULL DEFAULT now(),
        "updatedAt" TIMESTAMPTZ NOT NULL DEFAULT now(),
        CONSTRAINT "PK_users_id" PRIMARY KEY ("id")
      )
    `);
      await queryRunner.query(
        'CREATE INDEX "IDX_users_bcCustomerId" ON "app"."users" ("bcCustomerId")',
      );

      // Accounts table - stores linked provider accounts (google, facebook, password)
      await queryRunner.query(`
      CREATE TABLE "app"."accounts" (
        "id" uuid NOT NULL DEFAULT uuid_generate_v4(),
        "userId" character varying NOT NULL,
        "provider" character varying NOT NULL,
        "providerAccountId" character varying(255) NOT NULL,
        "createdAt" TIMESTAMPTZ NOT NULL DEFAULT now(),
        "updatedAt" TIMESTAMPTZ NOT NULL DEFAULT now(),
        CONSTRAINT "PK_accounts_id" PRIMARY KEY ("id"),
        CONSTRAINT "UQ_accounts_provider_providerAccountId" UNIQUE ("provider", "providerAccountId"),
        CONSTRAINT "FK_accounts_userId" FOREIGN KEY ("userId") REFERENCES "app"."users"("id") ON DELETE CASCADE ON UPDATE NO ACTION
      )
    `);
      await queryRunner.query('CREATE INDEX "IDX_accounts_userId" ON "app"."accounts" ("userId")');
    } finally {
      await queryRunner.query('RESET ROLE');
    }
  }

  public async down(queryRunner: QueryRunner): Promise<void> {
    await setMigrationRole(queryRunner);
    try {
      await queryRunner.query('DROP INDEX "app"."IDX_accounts_userId"');
      await queryRunner.query('DROP TABLE "app"."accounts"');
      await queryRunner.query('DROP INDEX "app"."IDX_users_bcCustomerId"');
      await queryRunner.query('DROP TABLE "app"."users"');
    } finally {
      await queryRunner.query('RESET ROLE');
    }
  }
}
